// src/components/SuperAdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getCompanyRegistrationRequests } from '../services/firestoreService';

const SuperAdminDashboard = ({ setNotification, createCompanyCallable, setSuperAdminClaimCallable, getAllCompaniesAndUsersCallable }) => {
  const [requests, setRequests] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [adminEmail, setAdminEmail] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  
  // Globale Stats (Gesamtsumme)
  const [globalStats, setGlobalStats] = useState(null);

  // State für Details einer expandierten Firma
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null); // { stats: {}, settings: {} }
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Lokaler State für Formulareingaben der Inaktivität
  const [localSettings, setLocalSettings] = useState({ inactivityDays: 30, enableInactivityReminder: false });

  // 1. Listener für Registrierungsanfragen
  useEffect(() => {
    const unsubscribe = getCompanyRegistrationRequests(setRequests);
    return () => unsubscribe();
  }, []);

  // 2. Globale Stats laden (mit expliziter Region)
  useEffect(() => {
      const loadGlobalStats = async () => {
          try {
              const functions = getFunctions(undefined, 'europe-west1');
              const getStatsFn = httpsCallable(functions, 'getGlobalStats');
              const result = await getStatsFn();
              setGlobalStats(result.data);
          } catch (error) {
              console.error("Fehler beim Laden der globalen Stats:", error);
          }
      };
      if (activeTab === 'overview') loadGlobalStats();
  }, [activeTab]);

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getAllCompaniesAndUsersCallable();
      setCompanies(result.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setNotification({ message: `Fehler beim Laden der Firmen: ${error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [getAllCompaniesAndUsersCallable, setNotification]);

  useEffect(() => {
    if (activeTab === 'companies' && companies.length === 0) {
      fetchCompanies();
    }
  }, [activeTab, companies.length, fetchCompanies]);

  const handleExpandCompany = async (companyId) => {
      if (expandedCompanyId === companyId) {
          setExpandedCompanyId(null); 
          return;
      }
      
      setExpandedCompanyId(companyId);
      setLoadingDetails(true);
      setCompanyDetails(null);

      try {
          const functions = getFunctions(undefined, 'europe-west1');
          const getDetailsFn = httpsCallable(functions, 'getCompanyDeepDetails');
          const result = await getDetailsFn({ targetCompanyId: companyId });
          
          setCompanyDetails(result.data);
          setLocalSettings(result.data.settings || { inactivityDays: 30, enableInactivityReminder: false });

      } catch (error) {
          setNotification({ message: `Fehler beim Laden der Details: ${error.message}`, type: 'error' });
      } finally {
          setLoadingDetails(false);
      }
  };

  const handleSaveCompanySettings = async (companyId) => {
      try {
          const functions = getFunctions(undefined, 'europe-west1');
          const saveFn = httpsCallable(functions, 'saveCompanyInactivitySettings');
          await saveFn({ targetCompanyId: companyId, settings: localSettings });
          setNotification({ message: "Einstellungen für diese Firma gespeichert.", type: 'success' });
      } catch (error) {
          setNotification({ message: `Fehler: ${error.message}`, type: 'error' });
      }
  };

  const handleRunInactivityCheck = async () => {
      if (!window.confirm("Jetzt den globalen Prüflauf starten? \n\nHinweis: Dies prüft ALLE Firmen. Es werden aber nur Mails gesendet, wenn die jeweilige Firma die Funktion aktiviert hat und der individuelle Zeitabstand überschritten ist.")) return;
      setIsLoading(true);
      try {
          const functions = getFunctions(undefined, 'europe-west1');
          const runFn = httpsCallable(functions, 'sendInactivityReminders');
          const result = await runFn();
          setNotification({ message: result.data.message, type: 'success' });
      } catch (error) {
          setNotification({ message: `Fehler: ${error.message}`, type: 'error' });
      } finally {
          setIsLoading(false);
      }
  };

  const handleApprove = async (request) => { setIsLoading(true); try { const result = await createCompanyCallable({ requestId: request.id }); setNotification({ message: result.data.message, type: 'success' }); } catch (error) { setNotification({ message: `Fehler: ${error.message}`, type: 'error' }); } finally { setIsLoading(false); } };
  const handleDeny = async (request) => { setIsLoading(true); try { setNotification({ message: `Anfrage von ${request.companyName} abgelehnt.`, type: 'success' }); } catch (error) { setNotification({ message: `Fehler: ${error.message}`, type: 'error' }); } finally { setIsLoading(false); } };
  const handleSetSuperAdmin = async (e) => { e.preventDefault(); if (!adminEmail) return; setIsLoading(true); try { const result = await setSuperAdminClaimCallable({ email: adminEmail }); setNotification({ message: result.data.message, type: 'success' }); setAdminEmail(''); } catch (error) { setNotification({ message: `Fehler: ${error.message}`, type: 'error' }); } finally { setIsLoading(false); } };

  return (
    <div className="p-5 bg-white dark:bg-gray-800 rounded-lg shadow-xl min-h-screen text-gray-900 dark:text-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h2 className="text-3xl font-bold">Super-Admin Dashboard</h2>
          <button 
            onClick={handleRunInactivityCheck}
            className="text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 px-4 py-2 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 transition shadow-sm border border-blue-200 dark:border-blue-700"
            title="Prüft alle Firmen und sendet Mails basierend auf deren individuellen Einstellungen."
          >
              ⚡ Globaler Inaktivitäts-Check ausführen
          </button>
      </div>

      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
        {['overview', 'requests', 'companies', 'admin'].map(tab => (
            <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-lg font-medium capitalize whitespace-nowrap ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'}`}
            >
                {tab === 'overview' ? 'Gesamtübersicht' : tab === 'requests' ? `Anfragen (${requests.length})` : tab === 'companies' ? 'Firmen & Details' : tab}
            </button>
        ))}
      </div>

      {isLoading && <p className="text-center py-8 text-blue-500 animate-pulse">Verarbeite...</p>}

      {/* TAB: OVERVIEW (GLOBAL SUMMARIES) */}
      {activeTab === 'overview' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 animate-fadeIn">
              <div className="bg-indigo-100 dark:bg-indigo-900/30 p-6 rounded-xl">
                  <p className="text-sm font-bold uppercase text-indigo-600 dark:text-indigo-300">Alle Protokolle</p>
                  <p className="text-4xl font-extrabold mt-2">{globalStats?.totalMeetings || 0}</p>
              </div>
              <div className="bg-green-100 dark:bg-green-900/30 p-6 rounded-xl">
                  <p className="text-sm font-bold uppercase text-green-600 dark:text-green-300">Alle Aufgaben</p>
                  <p className="text-4xl font-extrabold mt-2">{globalStats?.totalTasks || 0}</p>
              </div>
              <div className="bg-purple-100 dark:bg-purple-900/30 p-6 rounded-xl">
                  <p className="text-sm font-bold uppercase text-purple-600 dark:text-purple-300">Alle Projekte</p>
                  <p className="text-4xl font-extrabold mt-2">{globalStats?.totalProjects || 0}</p>
              </div>
              <div className="bg-pink-100 dark:bg-pink-900/30 p-6 rounded-xl">
                  <p className="text-sm font-bold uppercase text-pink-600 dark:text-pink-300">Alle User</p>
                  <p className="text-4xl font-extrabold mt-2">{globalStats?.totalUsers || 0}</p>
              </div>
          </div>
      )}

      {/* TAB: REQUESTS */}
      {activeTab === 'requests' && !isLoading && (
        <div className="space-y-4">
          {requests.length === 0 ? <p className="text-gray-500">Keine offenen Anfragen.</p> : requests.map(request => (
              <div key={request.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg flex justify-between items-center shadow-sm">
                <div>
                  <p className="font-bold text-lg">{request.companyName} ({request.subdomain})</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Admin: {request.adminUser.email}</p>
                </div>
                <div className="space-x-2">
                  <button onClick={() => handleApprove(request)} className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700">Genehmigen</button>
                  <button onClick={() => handleDeny(request)} className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700">Ablehnen</button>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* TAB: COMPANIES (HIER IST DIE LOGIK PRO FIRMA) */}
      {activeTab === 'companies' && !isLoading && (
        <div className="space-y-4">
          {companies.length === 0 ? <p className="text-gray-500">Keine Firmen registriert.</p> : companies.map(company => (
            <div key={company.id} className="bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-lg shadow-sm overflow-hidden transition-all duration-200">
              
              {/* Header der Firmenkarte - Klickbar zum Aufklappen */}
              <div 
                className={`p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${expandedCompanyId === company.id ? 'bg-blue-50 dark:bg-gray-600' : ''}`}
                onClick={() => handleExpandCompany(company.id)}
              >
                <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${expandedCompanyId === company.id ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                    <div>
                        <h3 className="font-bold text-lg text-blue-600 dark:text-blue-400">{company.name}</h3>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">ID: {company.id}</p>
                    </div>
                </div>
                <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{company.userCount} User</span>
                    <svg className={`w-6 h-6 transform transition-transform duration-300 ${expandedCompanyId === company.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>

              {/* Expandierter Bereich mit Stats & Settings für DIESE Firma */}
              {expandedCompanyId === company.id && (
                <div className="p-6 border-t dark:border-gray-600 bg-gray-50 dark:bg-gray-750 animate-slideDown">
                    {loadingDetails ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : companyDetails ? (
                        <div className="space-y-8">
                            
                            {/* 1. FIRMEN STATISTIKEN */}
                            <div>
                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Statistiken für {company.name}</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-l-4 border-indigo-500">
                                        <p className="text-xs text-gray-500 uppercase">Protokolle</p>
                                        <p className="text-2xl font-bold text-gray-800 dark:text-white">{companyDetails.stats.totalMeetings}</p>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-l-4 border-green-500">
                                        <p className="text-xs text-gray-500 uppercase">Aufgaben</p>
                                        <p className="text-2xl font-bold text-gray-800 dark:text-white">{companyDetails.stats.totalTasks}</p>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
                                        <p className="text-xs text-gray-500 uppercase">Projekte</p>
                                        <p className="text-2xl font-bold text-gray-800 dark:text-white">{companyDetails.stats.totalProjects}</p>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-l-4 border-pink-500">
                                        <p className="text-xs text-gray-500 uppercase">User</p>
                                        <p className="text-2xl font-bold text-gray-800 dark:text-white">{companyDetails.stats.totalUsers}</p>
                                    </div>
                                </div>
                            </div>

                            {/* 2. FIRMEN EINSTELLUNGEN (INAKTIVITÄT) */}
                            <div className="bg-blue-50 dark:bg-gray-800 p-6 rounded-xl border border-blue-100 dark:border-gray-600">
                                <h4 className="text-sm font-bold text-blue-700 dark:text-blue-300 uppercase tracking-widest mb-4 flex items-center">
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    Automatische Inaktivitäts-Erinnerung
                                </h4>
                                
                                <div className="flex flex-col md:flex-row gap-6 items-start md:items-center bg-white dark:bg-gray-900 p-4 rounded-lg shadow-sm">
                                    <div className="flex items-center space-x-3">
                                        <input 
                                            type="checkbox" 
                                            id={`enable-${company.id}`}
                                            className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                            checked={localSettings.enableInactivityReminder}
                                            onChange={(e) => setLocalSettings({...localSettings, enableInactivityReminder: e.target.checked})}
                                        />
                                        <label htmlFor={`enable-${company.id}`} className="font-medium cursor-pointer select-none">Funktion aktivieren</label>
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        <label className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Erinnerung senden nach:</label>
                                        <div className="relative flex items-center">
                                            <input 
                                                type="number" 
                                                className="w-20 p-2 pl-3 pr-8 border rounded dark:bg-gray-700 dark:border-gray-600 text-center font-bold focus:ring-2 focus:ring-blue-500 outline-none transition"
                                                value={localSettings.inactivityDays}
                                                onChange={(e) => setLocalSettings({...localSettings, inactivityDays: parseInt(e.target.value)})}
                                                min="1"
                                                disabled={!localSettings.enableInactivityReminder}
                                            />
                                            <span className="absolute right-3 text-xs text-gray-500 font-medium">Tagen</span>
                                        </div>
                                    </div>

                                    <div className="md:ml-auto w-full md:w-auto">
                                        <button 
                                            onClick={() => handleSaveCompanySettings(company.id)}
                                            className="w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 active:bg-blue-800 transition-colors text-sm font-bold flex items-center justify-center"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                            Speichern
                                        </button>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 italic ml-1">
                                    Hinweis: Wenn aktiviert, erhalten Benutzer der Firma <strong>{company.name}</strong> automatisch eine E-Mail, wenn sie sich länger als die angegebene Zeit nicht eingeloggt haben.
                                </p>
                            </div>

                        </div>
                    ) : (
                        <div className="text-center p-4 text-red-500 bg-red-50 rounded-lg">
                            Konnte Details nicht laden. Bitte versuche es erneut.
                        </div>
                    )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* TAB: ADMIN TOOLS (Super Admin zuweisen) */}
      {activeTab === 'admin' && (
        <div className="space-y-6 max-w-lg animate-fadeIn">
          <h3 className="text-xl font-bold border-b pb-2 mb-4">Super-Admin Status zuweisen</h3>
          <form onSubmit={handleSetSuperAdmin} className="flex flex-col gap-4 bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">E-Mail Adresse des Benutzers</label>
            <input type="email" placeholder="user@example.com" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className="p-3 border rounded-lg dark:bg-gray-800 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 outline-none" required />
            <button type="submit" disabled={isLoading} className="px-4 py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition shadow-md">
                {isLoading ? 'Wird verarbeitet...' : 'Super-Admin Rechte vergeben'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;